/* ================================================================ */
// FIELD COMPONENT | Field.js
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import React, {useState, useCallback, forwardRef} from 'react';
import PropTypes from 'prop-types';
// Material UI
import TextField from '@material-ui/core/TextField';
import makeStyles from '@material-ui/styles/makeStyles';
/* ========================================== */
// Constants
/* ========================================== */
const FieldTypes = {
    STRING: 'string',
    LATITUDE: 'lat',
    LONGITUDE: 'lon',
    INTEGER: 'int',
    URL: 'url',
    HTML: 'html',
    MEDIA: 'media'
};
const FieldParams = {
    [FieldTypes.INTEGER]: {
        helperText: 'Enter a valid number',
        type: 'number',
        parser: parseInt,
    },
    [FieldTypes.LATITUDE]: {
        helperText: 'Enter a valid longitude between -90 and 90',
        type: 'number',
        parser: parseFloat,
        min: -90,
        max: 90
    },
    [FieldTypes.LONGITUDE]: {
        helperText: 'Enter a valid longitude between -180 and 180',
        type: 'number',
        parser: parseFloat,
        min: -180,
        max: 180
    },
    [FieldTypes.URL]: {
        helperText: 'Enter a valid url',
        type: 'url'
    },
    [FieldTypes.HTML]: {
        helperText: 'Enter valid HTML or Markdown',
        type: 'text'
    }
};
/* ========================================== */
// Style Settings
/* ========================================== */
const useStyles = makeStyles(theme => ({
    root: {
        fontSize: '1rem',
        //background: hexToRgba(theme.palette.background.paper, 0.9),
        //width: 'calc(100% + 7em)',
        width: '100%',
        margin: 0,
        '&.overflow': {
            width: 'calc(100% + 7em)'
        }
    },
    input: {
        position: 'relative',
        top: 0,
        color: theme.palette.common.black,
        background: theme.palette.common.white,  
        border: `0.05em ${theme.palette.background.contentFrame} solid`,
        borderRadius: '0 1em 1em 1em'
    },
    underline: {
        borderRadius: '0 1em 1em 1em'
    },
    label: {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.common.black
    },
    helperText: {
        width: '100%',
        fontSize: '0.9rem',
        color: theme.palette.common.black,
        backgroundColor: theme.palette.common.white
    }
}));
/* ========================================== */
/**
 * Field Component
 * ---------------
 * Handles editable text field and validation
 * for given field type
 * 
 * @param {object} props component props
 * @param {string} props.label field label
 * @param {string} props.helperText helper text
 * @param {string} props.type field type
 * @param {any} props.value field value
 * @param {func} props.onChange callback on field change
 * @param {func} props.onUpdate callback on field blur / submit
 */
export const Field = ({
    label,
    helperText,
    type,
    value,
    onChange,
    onUpdate
}, ref) => {
    const classes = useStyles();
    const [currentValue, setCurrentValue] = useState(value);
    const [isError, setIsError] = useState(false);
    /* ========================================== */
    /**
     * Validate
     * --------
     * Checks value for given type
     * is a valid entry
     * 
     * @param {string} type <string|number|lon|lat|integer|url|html> 
     * @param {any} value data value
     */
    const validate = (type, value) => {
        switch(type) {
        case FieldTypes.LONGITUDE:
            // Longitude Validation
            return (value >= FieldParams[FieldTypes.LONGITUDE].min && value <= FieldParams[FieldTypes.LONGITUDE].max);
        case FieldTypes.LATITUDE:
            // Latitude Validation
            return (value >= FieldParams[FieldTypes.LATITUDE].min && value <= FieldParams[FieldTypes.LATITUDE].max);
        case FieldTypes.INTEGER:
            return Number.isInteger(value);
        default:
            return true;
        }
    };
    /* ======================== */
    // HANDLERS
    /* ======================== */
    /**
     * Handle Change
     * -------------
     * Parse and check new value by type
     * and return in callback
     * 
     * @param {Event.onChange} event onChange event
     */
    const handleChange = (event) => {
        try {
            const newValue = FieldParams[type]?.parser ? FieldParams[type].parser(event.target.value) : event.target.value;
            //setCurrentValue(newValue);
            console.log(newValue);
            const isValid = validate(type, newValue);
            setCurrentValue(newValue);
            isValid && onChange && onChange(newValue);
            setIsError(!isValid);
        } catch (e) {
            console.log(e);
            setIsError(true);
        }
    };
    const handleUpdate = useCallback((event) => {
        console.log(event, isError, currentValue);
        if(isError) {
            ref.current.focus();
        } else onUpdate && onUpdate(currentValue);
    }, [isError, currentValue]);
    const handleKeyDown = useCallback((event) => {
        if(event.keyCode === 13) { // ENTER
            console.log(event, isError, currentValue);
            if(isError) {
                ref.current.focus();
            } else onUpdate && onUpdate(currentValue);
        }
    }, [isError, currentValue]);
    console.log('FIELD', label, type, value, isError);
    /* ======================== */
    // RENDER
    /* ======================== */
    return (
        <TextField
            className={classes.root}
            error={isError}
            label={label}
            type={FieldParams[type]?.type || 'text'}
            inputRef={ref}
            FormHelperTextProps={{
                classes: {root: classes.helperText}
            }}
            InputProps={{
                classes: {notchedOutline: classes.underline, input: classes.input}
            }}
            InputLabelProps={{
                classes: {root: classes.label}
            }}
            onChange={handleChange}
            onBlur={handleUpdate}
            onKeyDown={handleKeyDown}
            id={`${label}-field`}
            value={currentValue}
            margin="dense"
            variant="outlined"
            helperText={helperText || FieldParams[type]?.helperText || ''}
        />
    );
};
/* ======================== */
// PROPS
/* ======================== */
Field.propTypes = {
    label: PropTypes.string.isRequired,
    helperText: PropTypes.string,
    type: PropTypes.oneOf(Object.values(FieldTypes)).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    onChange: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired
};
Field.propTypes = {
    type: 'string',
    value: ''
};
Field.displayName = 'Field';
/* ========================================== */
// Export
/* ========================================== */
export default forwardRef(Field);